import React from 'react';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { useForm } from 'react-hook-form';
import FilterModal from '../../../components/ModernFilters/FilterModal';
import CheckboxList from '../../../components/ModernFilters/CheckboxList';
import CameraTypeList from '../../../components/ModernFilters/CameraTypeList';
import CameraIntegrationTypeList from '../../../components/ModernFilters/cameras/CameraIntegrationTypeList';
import CameraOriginList from '../../../components/ModernFilters/cameras/CameraOriginList';
import { translate } from '../../../services/translate';
import { CamerasParamList } from '../../../typings/Params';
import { CameraType, IntegrationType } from '../../../schemas/cameras';
import RegionsList from '../../../components/ModernFilters/RegionsList';
import CameraTagList from '../../../components/ModernFilters/cameras/CameraTagList';

interface Props {
    filters: CamerasParamList['CamerasList'];
    setFilters: React.Dispatch<React.SetStateAction<CamerasParamList['CamerasList']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

type FiltersForm = {
    status: ('online' | 'offline')[];
    type: CameraType | null;
    tagsIds: number[];
    integrationType: IntegrationType[];
    origin: string | null;
    regions: {
        type: string;
        value: number;
    };
};

function Filter({ filters, setFilters, textFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<CamerasParamList, 'CamerasList'>>>();
    const form = useForm<FiltersForm>({
        mode: 'onChange',
        defaultValues: {
            status: [
                ...(filters.online ? ['online' as const] : []),
                ...(filters.offline ? ['offline' as const] : []),
            ],
            type: filters.type ?? null,
            tagsIds: filters.tagsIds ?? [],
            integrationType: filters.integrationType ?? [],
            origin: filters.origin ?? null,
            regions: { type: filters.polygonType ?? '', value: filters.polygon ?? 0 },
        },
    });

    const handleFilter = (values: FiltersForm) => {
        const online = values.status.includes('online');
        const offline = values.status.includes('offline');
        const params = {
            textFilter: textFilter || undefined,
            online,
            offline: offline ? true : undefined,
            tagsIds: values.tagsIds.length ? values.tagsIds : undefined,
            type: values.type ?? undefined,
            integrationType: values.integrationType.length ? values.integrationType : undefined,
            origin: values.origin?.trim() ? values.origin : undefined,
            polygonType: values.regions.type || undefined,
            polygon: values.regions.value !== 0 ? values.regions.value : undefined,
        };

        setFilters({ ...filters, page: 0, ...params });
        navigation.setParams({ ...params, online: online ? true : undefined, page: 1 });
    };

    return <FilterModal
        form={form}
        onFilters={handleFilter}
        cleanValues={{
            status: [],
            type: null,
            tagsIds: [],
            integrationType: [],
            origin: null,
            regions: { type: '', value: 0 },
        }}
        applyDisabled={form.watch('regions.type') !== '' && form.watch('regions.value') === 0}
        filters={[{
            label: translate('cameraInfoLabel'),
            filters: [{
                componentHandlesCount: false,
                name: 'status',
                label: translate('status'),
                getCount: CheckboxList.getCount,
                component: ({ name }) => <CheckboxList name={name} items={[
                    { label: translate('online'), value: 'online' },
                    { label: translate('offline'), value: 'offline' },
                ]} />,
            }, {
                componentHandlesCount: true,
                name: 'type',
                label: translate('cameraType'),
                component: CameraTypeList,
            }, {
                componentHandlesCount: true,
                name: 'tagsIds',
                label: translate('tags'),
                component: CameraTagList,
            }, {
                componentHandlesCount: true,
                name: 'integrationType',
                label: translate('integrationType'),
                component: CameraIntegrationTypeList,
            }, {
                componentHandlesCount: true,
                name: 'origin',
                label: translate('origin'),
                component: CameraOriginList,
            }, {
                componentHandlesCount: true,
                name: 'regions',
                label: translate('regions'),
                component: RegionsList,
            }],
        }]}
    />;
}

export default Filter;
